import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Initiative } from '../../types/database';
import { InitiativeModal } from './InitiativeModal';
import { InitiativeGoalScore } from '../../utils/initiativeGoalScoring';

export const InitiativeList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const db = useDatabase();
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openInitiativeModal, setOpenInitiativeModal] = useState(false);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [allGoalScores, setAllGoalScores] = useState<InitiativeGoalScore[]>([]);

  // Get status filter from URL
  const statusFilter = new URLSearchParams(location.search).get('status');

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value;
    if (newStatus === 'All') {
      navigate('/initiatives');
    } else {
      navigate(`/initiatives?status=${newStatus}`);
    }
  };

  const loadInitiatives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await db.getInitiatives();
      if (error) throw error;
      setInitiatives(data || []);

      // Load scores from database instead of localStorage
      const { data: scores, error: scoresError } = await db.getInitiativeGoalScores();
      if (scoresError) {
        console.error('Error loading initiative goal scores:', scoresError);
      } else if (scores) {
        setAllGoalScores(scores);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load initiatives');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadInitiatives();
  }, [loadInitiatives]);

  // Filter initiatives based on status
  const filteredInitiatives = statusFilter
    ? initiatives.filter(initiative => initiative.status === statusFilter)
    : initiatives;

  // Sort initiatives alphabetically by name
  const sortedInitiatives = [...filteredInitiatives].sort((a, b) => 
    a.name.localeCompare(b.name)
  );

  const handleInitiativeClick = (initiativeId: string) => {
    navigate(`/initiatives/${initiativeId}`);
  };

  const handleAddInitiative = async (formData: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await db.createInitiative(formData);
      if (error) throw error;
      await loadInitiatives();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create initiative');
    }
  };

  const handleEditInitiative = async (formData: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) => {
    if (!selectedInitiative) return;
    
    try {
      const { error } = await db.updateInitiative(selectedInitiative.id, formData);
      if (error) throw error;
      await loadInitiatives();
      setSelectedInitiative(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update initiative');
    }
  };

  const handleMoreClick = (event: React.MouseEvent, initiative: Initiative) => {
    event.stopPropagation(); // Prevent the list item click
    setSelectedInitiative(initiative);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={statusFilter ? `${statusFilter} initiatives` : 'Initiatives'}>
      <Box sx={{ flexGrow: 1, p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter || 'All'}
                label="Status"
                onChange={handleFilterChange}
                startAdornment={<FilterIcon sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenInitiativeModal(true)}
              sx={{ borderRadius: 2, height: 32 }}
            >
              New
            </Button>
          </Box>
        </Box>

        <Card>
          <CardContent>
            <List sx={{ p: 0 }}>
              {sortedInitiatives.map((initiative) => (
                <React.Fragment key={initiative.id}>
                  <ListItem
                    onClick={() => handleInitiativeClick(initiative.id)}
                    sx={{
                      py: 1.5,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1">{initiative.name}</Typography>
                          <Chip
                            label={initiative.status}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: 
                                initiative.status === 'Active' ? 'rgba(76, 175, 80, 0.1)' :
                                initiative.status === 'Completed' ? 'rgba(158, 158, 158, 0.1)' :
                                'rgba(255, 152, 0, 0.1)',
                              color: 
                                initiative.status === 'Active' ? 'rgba(76, 175, 80, 0.9)' :
                                initiative.status === 'Completed' ? 'rgba(158, 158, 158, 0.9)' :
                                'rgba(255, 152, 0, 0.9)',
                              border: '1px solid',
                              borderColor: 
                                initiative.status === 'Active' ? 'rgba(76, 175, 80, 0.3)' :
                                initiative.status === 'Completed' ? 'rgba(158, 158, 158, 0.3)' :
                                'rgba(255, 152, 0, 0.3)',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {initiative.description}
                          </Typography>
                          {(() => {
                            const initiativeScores = allGoalScores.filter(score => score.initiativeId === initiative.id);
                            const topScores = initiativeScores
                              .sort((a, b) => {
                                // First sort by category order: personal, professional, financial
                                const categoryOrder = { personal: 0, professional: 1, financial: 2 };
                                const categoryDiff = categoryOrder[a.goalCategory as keyof typeof categoryOrder] - categoryOrder[b.goalCategory as keyof typeof categoryOrder];
                                if (categoryDiff !== 0) return categoryDiff;
                                // Then sort by score (highest first) within each category
                                return b.score - a.score;
                              })
                              .slice(0, 3); // Show top 3 scores
                            
                            return topScores.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {topScores.map((score) => (
                                  <Chip
                                    key={`${score.initiativeId}-${score.goalId}`}
                                    label={`${score.goalCategory}: ${score.score}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: '20px',
                                      borderColor: score.score >= 70 ? 'success.main' : 
                                                  score.score >= 40 ? 'warning.main' : 'error.main',
                                      color: score.score >= 70 ? 'success.main' : 
                                            score.score >= 40 ? 'warning.main' : 'error.main',
                                      '& .MuiChip-label': {
                                        px: 0.5
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          })()}
                        </Box>
                      }
                    />
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMoreClick(e, initiative)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItem>
                  <Divider component="li" sx={{ borderColor: 'divider' }} />
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>

        <InitiativeModal
          open={openInitiativeModal}
          onClose={() => setOpenInitiativeModal(false)}
          onSubmit={handleAddInitiative}
        />

        <InitiativeModal
          open={!!selectedInitiative}
          onClose={() => setSelectedInitiative(null)}
          onSubmit={handleEditInitiative}
          initiative={selectedInitiative}
        />
      </Box>
    </DashboardLayout>
  );
}; 